import React, { useState, useMemo, useRef, useEffect } from 'react';
import SearchHeader from './SearchHeader';
import SearchInfo from './SearchInfo';
import { RelevanceFilters, RelevanceFilter } from './RelevanceFilters';
import FiltersModal from './FiltersModal';
import { FiltersState } from '../../hooks/useFilters';
import SearchFromSourcesHeader from '../../relevanter/components/SearchFromSourcesHeader';
import { useAuthStore } from '../../store/authStore';
import { getSourceOptions, normalizeSortByForSource } from '../../utils/filterUtils';
import { getSources } from '../../relevanter/services/api';
import { getDefaultFiltersForSource } from '../../sources/filterPolicy';

interface SearchInterfaceProps {
  filters: FiltersState;
  onFiltersChange: (filters: Partial<FiltersState>) => void;
  onSearch: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchTitle: string;
  candidateCount: number;
  totalFound?: number; // Total candidates in HH before relevance filtering
  filteredCount?: number; // Candidates that passed relevance filter
  searchMode?: string;
  onSearchModeChange?: (mode: string) => void;
  shouldAutoParse?: boolean;
  parsedSummary?: any;
  setParsedSummary?: (summary: any) => void;
  onHHSettingsClick?: () => void;
  onHHAuthClick?: () => void;
  onATSSettingsClick?: () => void;
  onHHVacanciesClick?: () => void;
  isModalSearchActive?: boolean;
  setIsModalSearchActive?: (isActive: boolean) => void;
  selectedRelevance?: RelevanceFilter;
  onRelevanceChange?: (value: RelevanceFilter) => void;
  relevancePageSize?: number;
  onRelevancePageSizeChange?: (size: number) => void;
  showRelevanceReset?: boolean;
  onRelevanceReset?: () => void;
  scoringEnabled?: boolean;
  onScoringEnabledChange?: (enabled: boolean) => void;
  hideHeader?: boolean;
  isApproximate?: boolean;
  sourceDropdown?: React.ReactNode;
}

export const SearchInterface: React.FC<SearchInterfaceProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  searchValue,
  onSearchChange,
  searchTitle,
  candidateCount,
  totalFound,
  filteredCount,
  searchMode = 'custom',
  onSearchModeChange,
  shouldAutoParse,
  parsedSummary,
  setParsedSummary,
  onHHSettingsClick,
  onHHAuthClick,
  onATSSettingsClick,
  onHHVacanciesClick,
  isModalSearchActive,
  setIsModalSearchActive,
  selectedRelevance,
  onRelevanceChange,
  relevancePageSize,
  onRelevancePageSizeChange,
  showRelevanceReset = false,
  onRelevanceReset,
  scoringEnabled,
  onScoringEnabledChange,
  hideHeader = false,
  isApproximate,
  sourceDropdown,
}) => {
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [scrollToSection, setScrollToSection] = useState<string | undefined>();
  const { token } = useAuthStore();
  const onSearchRef = useRef(onSearch);

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    if (setIsModalSearchActive) {
      setIsModalSearchActive(filtersModalOpen);
    }
  }, [filtersModalOpen, setIsModalSearchActive]);

  // Автоматически открываем фильтры при переходе с вакансии (только один раз)
  // ОТКЛЮЧЕНО: Теперь фильтры предзаполнены из URL, модалка не нужна
  // useEffect(() => {
  //   if (
  //     searchMode === 'vacancy' &&
  //     filters.vacancyDescription &&
  //     !filtersModalOpen &&
  //     !hasShownFiltersForVacancy.current
  //   ) {
  //     console.log('Auto-opening filters for vacancy search mode (first time)');
  //     hasShownFiltersForVacancy.current = true;
  //     setFiltersModalOpen(true);
  //   }
  // }, [searchMode, filters.vacancyDescription, filtersModalOpen]);

  // Generate filter chips from active filters
  const filterChips = useMemo(() => {
    const chips: Array<{
      id: string;
      label: string;
      value: string;
      onRemove: () => void;
    }> = [];




    return chips;
  }, []);

  // Загружаем доступные источники через API (проверяет наличие токенов)
  const [availableSources, setAvailableSources] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (sourceDropdown) return;
    if (!token) return;
    let cancelled = false;
    getSources()
      .then((data: any) => {
        if (cancelled) return;
        const available = new Set<string>();
        (data.sources || []).forEach((s: any) => {
          if (s.available) available.add(s.id.toLowerCase());
        });
        setAvailableSources(available);
      })
      .catch(() => {
        // При ошибке — только HH (безопасный fallback)
        if (!cancelled) setAvailableSources(new Set(['hh']));
      });
    return () => { cancelled = true; };
  }, [sourceDropdown, token]);

  const sourceOptions = useMemo(() => {
    const all = getSourceOptions();
    if (sourceDropdown) return all;
    if (!availableSources) return all.filter(opt => opt.value === 'hh'); // Пока грузится — только HH
    return all.filter(opt => availableSources.has(opt.value));
  }, [availableSources, sourceDropdown]);

  useEffect(() => {
    if (sourceDropdown) return;
    // Откатываем source в 'hh' только когда availableSources УЖЕ загружены
    // и реально не содержат выбранный source. Иначе при клике на E-Staff/Skillaz
    // во время загрузки availableSources sourceOptions=[HH only] и пользовательский
    // выбор моментально сбрасывается обратно в HH.
    if (!availableSources) return;
    if (filters.source !== 'hh' && !sourceOptions.find(opt => opt.value === filters.source)) {
      const patch: Partial<FiltersState> = { source: 'hh' };
      const normalizedSortBy = normalizeSortByForSource('hh', filters.sortBy);
      if (normalizedSortBy) {
        patch.sortBy = normalizedSortBy;
      }
      onFiltersChange(patch);
    }
  }, [filters.source, filters.sortBy, sourceOptions, onFiltersChange, availableSources, sourceDropdown]);

  const searchParams = [
    {
      id: 'source',
      label: 'Источник поиска',
      value: filters.source || 'hh',
      options: sourceOptions,
      onChange: (value: string) => {
        const target = value as any;
        // TASKNEIROKLYUCH-790: при выходе из E-Staff чистим
        // expectedPositionName — input под этим контролом виден только
        // при source='estaff' (см. Filters.tsx), и иначе застрянет
        // в state, ломая `getActiveFiltersCount` (счётчик активных)
        // и улетит в API как лишний параметр.
        const patch: Partial<FiltersState> = { source: target };
        if (target !== 'estaff' && filters.expectedPositionName) {
          patch.expectedPositionName = '';
        }
        if (target !== 'hh' && filters.hideViewedByCompany) {
          patch.hideViewedByCompany = false;
        }
        const normalizedSortBy = normalizeSortByForSource(target, filters.sortBy);
        if (normalizedSortBy) {
          patch.sortBy = normalizedSortBy;
        }
        onFiltersChange(patch);
      }
    },
    {
      id: 'period',
      label: 'За месяц',
      value: filters.excludeUpdatedPeriod || 'all',
      options: [
        { value: 'all', label: 'За все время' },
        { value: 'last_month', label: 'За месяц' },
        { value: 'last_3_months', label: 'За 3 месяца' }
      ],
      onChange: (value: string) => onFiltersChange({ excludeUpdatedPeriod: value })
    },
    {
      id: 'sort',
      label: filters.source === 'estaff' ? 'По дате статуса' : 'По релевантности',
      value: filters.sortBy || 'relevance',
      options: filters.source === 'estaff'
        ? [
          { value: 'status_date_desc', label: 'Дата статуса: новые' },
          { value: 'status_date_asc', label: 'Дата статуса: старые' },
          { value: 'relevance', label: 'По релевантности' },
          { value: 'salary_desc', label: 'По убыванию зарплат' },
          { value: 'salary_asc', label: 'По возрастанию зарплаты' }
        ]
        : [
          { value: 'relevance', label: 'По релевантности' },
          { value: 'date_updated', label: 'По дате обновления' },
          { value: 'salary_desc', label: 'По убыванию зарплат' },
          { value: 'salary_asc', label: 'По возрастанию зарплаты' }
        ],
      onChange: (value: string) => onFiltersChange({ sortBy: value })
    },
    {
      // TASKNEIROKLYUCH-645: этот дропдаун — единый источник правды для
      // размера страницы: client rowsPerPage в ResultsTable И backend per_page
      // (createApiQuery шлёт filters.limit как pageSize). Target count для
      // relevance-поиска — отдельный контрол «Количество кандидатов» в
      // RelevanceFilters, живёт в локальном state relevancePageSize.
      id: 'limit',
      label: '20 резюме',
      value: String(filters.limit || 20),
      options: [
        { value: '10', label: '10 резюме' },
        { value: '20', label: '20 резюме' },
        { value: '50', label: '50 резюме' },
        { value: '100', label: '100 резюме' }
      ],
      onChange: (value: string) => {
        onFiltersChange({ limit: parseInt(value) });
      }
    }
  ].filter(param => {
    // Avito MVP не поддерживает общий sort.
    if (param.id === 'sort' && filters.source === 'avito') return false;
    // Avito использует свой updated_from/updated_to в полной модалке.
    if (param.id === 'period' && filters.source === 'avito') return false;
    return true;
  });

  const handleClearFilters = () => {
    onSearchChange('');
    onFiltersChange({ ...(getDefaultFiltersForSource(filters.source || 'hh') as any) });
    setTimeout(() => {
      onSearchRef.current();
    }, 50);
  };

  const handleChipClick = (chipId: string) => {
    // This will be handled by the FilterButton component
    console.log('Chip clicked:', chipId);
  };

  return (
    <div className="content-stretch flex flex-col items-start relative w-full px-2">
      {/* Main Search Container */}
      <div className="bg-[#f5f6f1] content-stretch flex flex-col gap-[10px] items-start relative rounded-t-xl shrink-0 w-full">
        {!hideHeader && (
          <div className="pt-4 ml-2 shrink-0 w-full">
            <SearchFromSourcesHeader
              source={filters.source || 'hh'}
              onHHSettingsClick={onHHSettingsClick}
              onHHAuthClick={onHHAuthClick}
              onATSSettingsClick={onATSSettingsClick}
            />
          </div>
        )}

        <SearchHeader
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          onSearch={onSearch}
          onClearFilters={handleClearFilters}
          onToggleFilters={(section) => {
            setScrollToSection(section);
            setFiltersModalOpen(true);
          }}
          filtersOpen={false}
          filters={filters}
          onFiltersChange={onFiltersChange}
          filterChips={filterChips}
          onChipClick={handleChipClick}
          sourceDropdown={sourceDropdown}
        />

        {/* Relevance Filters */}
        <RelevanceFilters
          candidateCount={candidateCount}
          selectedRelevance={selectedRelevance}
          onRelevanceChange={onRelevanceChange}
          relevancePageSize={relevancePageSize}
          onRelevancePageSizeChange={onRelevancePageSizeChange}
          showReset={showRelevanceReset}
          onReset={onRelevanceReset}
          scoringEnabled={scoringEnabled}
          onScoringEnabledChange={onScoringEnabledChange}
          hideViewedByCompany={filters.source === 'hh' ? (filters.hideViewedByCompany || false) : false}
          onHideViewedByCompanyChange={filters.source === 'hh' ? (enabled) => onFiltersChange({ hideViewedByCompany: enabled }) : undefined}
          onlyMyCandidates={filters.source === 'estaff' ? (filters.onlyMyCandidates || false) : false}
          onOnlyMyCandidatesChange={filters.source === 'estaff' ? (enabled) => onFiltersChange({ onlyMyCandidates: enabled }) : undefined}
        />

        {/* Search Info - внутри основного контейнера */}
        <SearchInfo
          title=""
          candidateCount={candidateCount}
          totalFound={totalFound}
          filteredCount={filteredCount}
          isRelevanceFiltered={!!selectedRelevance && typeof totalFound === 'number'}
          isApproximate={isApproximate}
          searchParams={searchParams.filter(p => p.id !== 'source')}
        />
      </div>

      {/* Filters Modal */}
      <FiltersModal
        isOpen={filtersModalOpen}
        onClose={() => {
          setFiltersModalOpen(false);
          setScrollToSection(undefined);
        }}
        filters={filters}
        onFiltersChange={onFiltersChange}
        onSearch={() => {
          setFiltersModalOpen(false);
          setScrollToSection(undefined);
          onSearch();
        }}
        onClearFilters={() => {
          handleClearFilters();
          setFiltersModalOpen(false);
          setScrollToSection(undefined);
        }}
        searchMode={searchMode}
        onSearchModeChange={onSearchModeChange}
        shouldAutoParse={shouldAutoParse}
        parsedSummary={parsedSummary}
        setParsedSummary={setParsedSummary}
        scrollToSection={scrollToSection}
      />
    </div>
  );
};

export default SearchInterface;
